import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { createServiceSchema } from "../../schemavalidations/serviceschema";
import {
  CreateService,
  Service,
  ServiceAccounting,
  Zone,
  Band,
} from "../../types/service";
import {
  INVOICE_DETAIL_KEYS,
  INVOICE_DETAIL_SAMPLES,
  previewDetailDescription,
} from "../../constants/invoiceDetailFields";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import {
  Plus,
  X,
  Wrench,
  Grid3x3,
  Pencil,
  Calculator,
  ReceiptText,
} from "lucide-react";
import { DirectorySelect } from "../directory/DirectorySelect";
import { GeneralServiceSelect } from "./GeneralServiceSelect";

interface ServiceFormProps {
  service?: Service;
  onSubmit: (data: CreateService | Partial<Service>) => Promise<void>;
  loading: boolean;
  isEditMode?: boolean;
  onTypeChange?: (type: string) => void;
  /**
   * Searches the General (partnerId = null) side of this same directory. A new
   * client-specific rate is copied from one of those entries rather than typed
   * from scratch — see `applyGeneralService`.
   */
  fetchGeneralServices?: (params: {
    search: string;
    page: number;
    limit: number;
  }) => Promise<{ services: Service[] }>;
}

interface FormValues {
  name: string;
  description?: string | null;
  type: string;
  price?: string | null;
  tax: string;
  partnerId?: string | null;
  // The accounting mapping, flat rather than nested: react-hook-form registers
  // these three like any other box, and they are reassembled on submit.
  accountingVatCode: string;
  accountingIncomeAccount: string;
}

/** The two codes off a service, as the form's flat fields. */
const accountingFields = (service?: Service | null) => ({
  accountingVatCode: service?.accounting?.vatCode ?? "",
  accountingIncomeAccount: service?.accounting?.incomeAccount ?? "",
});

const EMPTY_ACCOUNTING_FIELDS = {
  accountingVatCode: "",
  accountingIncomeAccount: "",
};

interface FormZone {
  name: string;
  postalFrom: string;
  postalTo: string;
}

interface FormBand {
  label: string;
  minTn: number | string;
  maxTn: number | string;
  ldm: number | string;
}

interface PriceMatrixIndexCell {
  zoneIndex: number;
  bandIndex: number;
  price: number;
}

/**
 * Matches the disabled styling of the shared Input/Select so a locked zone or
 * band row reads the same as any other read-only field in the app. The raw
 * <input>s in those rows predate that component, hence the literal.
 */
const LOCKED_FIELD_CLASSES = "bg-[#F5F4F0] text-[#8A9590] cursor-not-allowed";

const PREFILLED_ZONES: FormZone[] = [
  { name: "Tallinn", postalFrom: "10001", postalTo: "13999" },
  { name: "Harjumaa", postalFrom: "74001", postalTo: "76999" },
  { name: "Lääne-Harju", postalFrom: "76001", postalTo: "76999" },
  { name: "Ida-Viru", postalFrom: "20001", postalTo: "44999" },
  { name: "Lääne-Viru", postalFrom: "44001", postalTo: "49999" },
  { name: "Pärnumaa", postalFrom: "80001", postalTo: "89999" },
  { name: "Tartumaa", postalFrom: "50001", postalTo: "62999" },
  { name: "Viljandimaa", postalFrom: "69001", postalTo: "72999" },
  { name: "Võrumaa", postalFrom: "64001", postalTo: "66999" },
  { name: "Valgamaa", postalFrom: "66001", postalTo: "68999" },
  { name: "Põlvamaa", postalFrom: "63001", postalTo: "64999" },
  { name: "Jõgevamaa", postalFrom: "48001", postalTo: "49999" },
  { name: "Järvamaa", postalFrom: "72001", postalTo: "73999" },
  { name: "Raplamaa", postalFrom: "78001", postalTo: "79999" },
  { name: "Läänemaa", postalFrom: "90001", postalTo: "92999" },
  { name: "Saaremaa", postalFrom: "93001", postalTo: "94999" },
];

// ---------------------------------------------------------------------------
// ZoneCombobox
// ---------------------------------------------------------------------------
interface ZoneComboboxProps {
  value: string;
  onChange: (name: string) => void;
  onSelect: (zone: FormZone) => void;
  placeholder: string;
  disabled?: boolean;
}

const ZoneCombobox: React.FC<ZoneComboboxProps> = ({
  value,
  onChange,
  onSelect,
  placeholder,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = value.trim()
    ? PREFILLED_ZONES.filter((z) =>
        z.name.toLowerCase().includes(value.toLowerCase()),
      )
    : PREFILLED_ZONES;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => !disabled && setOpen(true)}
        placeholder={placeholder}
        className={`w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? LOCKED_FIELD_CLASSES : ""
        }`}
      />
      {!disabled && open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtered.map((zone) => (
            <li
              key={zone.name}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(zone);
                setOpen(false);
              }}
              className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 cursor-pointer select-none"
            >
              <span className="font-medium text-gray-800">{zone.name}</span>
              <span className="text-xs text-gray-400 ml-3 shrink-0 tabular-nums">
                {zone.postalFrom} – {zone.postalTo}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PriceTable
// ---------------------------------------------------------------------------
interface PriceTableProps {
  zones: FormZone[];
  bands: FormBand[];
  prices: Record<string, string>;
  onChange: (key: string, value: string) => void;
  readonly?: boolean;
  weightLdmLabel: string;
  zonePrefix: string;
  bandPrefix: string;
  allCellsNote: string;
}

const PriceTable: React.FC<PriceTableProps> = ({
  zones,
  bands,
  prices,
  onChange,
  readonly = false,
  weightLdmLabel,
  zonePrefix,
  allCellsNote,
}) => {
  if (zones.length === 0 || bands.length === 0) return null;

  return (
    <div className="w-full overflow-x-auto mt-4">
      <table className="w-full border-collapse border border-gray-300 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th
              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300 bg-gray-100 whitespace-nowrap"
              style={{ width: "180px", minWidth: "180px" }}
            >
              {weightLdmLabel}
            </th>
            {zones.map((zone, zIdx) => (
              <th
                key={zIdx}
                className="px-3 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300 whitespace-nowrap"
              >
                {zone.name || `${zonePrefix} ${zIdx + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bands.map((band, bIdx) => (
            <tr key={bIdx} className="hover:bg-gray-50">
              <td
                className="px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 bg-gray-50 whitespace-nowrap"
                style={{ width: "180px", minWidth: "180px" }}
              >
                {band.label || `Band ${bIdx + 1}`}
              </td>
              {zones.map((_, zIdx) => {
                const key = `${zIdx}-${bIdx}`;
                const val = prices[key] ?? "";
                const isEmpty = val === "" || val === "0";
                return (
                  <td key={zIdx} className="border border-gray-300 p-0">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={val}
                      readOnly={readonly}
                      onChange={(e) => onChange(key, e.target.value)}
                      placeholder="0"
                      className={`w-full px-2 py-2 text-sm text-center border-0 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-colors ${
                        isEmpty && !readonly
                          ? "bg-red-50 text-red-600"
                          : "bg-white"
                      } ${readonly ? "cursor-not-allowed bg-gray-50" : ""}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!readonly && (
        <p className="text-xs text-gray-500 mt-1">{allCellsNote}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Matrix helpers
// ---------------------------------------------------------------------------

/**
 * Saved matrix cells reference zones/bands by their Mongoose `_id`; the form
 * works in array indexes. Used both when loading a service for edit and when
 * copying one into a new client-specific rate.
 */
const toIndexedPrices = (service: Service): Record<string, string> => {
  const map: Record<string, string> = {};
  service.priceMatrix?.forEach((cell) => {
    const zIdx = service.zones?.findIndex((z) => z._id === cell.zoneId) ?? -1;
    const bIdx = service.bands?.findIndex((b) => b._id === cell.bandId) ?? -1;
    if (zIdx !== -1 && bIdx !== -1) map[`${zIdx}-${bIdx}`] = String(cell.price);
  });
  return map;
};

const toFormZones = (service: Service): FormZone[] =>
  (service.zones || []).map((z) => ({
    name: z.name,
    postalFrom: z.postalFrom,
    postalTo: z.postalTo,
  }));

const toFormBands = (service: Service): FormBand[] =>
  (service.bands || []).map((b) => ({
    label: b.label,
    minTn: b.minTn,
    maxTn: b.maxTn,
    ldm: b.ldm,
  }));

// ---------------------------------------------------------------------------
// ServiceForm
// ---------------------------------------------------------------------------
export const ServiceForm: React.FC<ServiceFormProps> = ({
  service,
  onSubmit,
  loading,
  isEditMode = false,
  onTypeChange,
  fetchGeneralServices,
}) => {
  const { t } = useTranslation(["services", "common"]);
  const [readonly, setReadonly] = useState(isEditMode);
  const [zones, setZones] = useState<FormZone[]>([]);
  const [bands, setBands] = useState<FormBand[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [matrixErrors, setMatrixErrors] = useState<string[]>([]);
  /**
   * The container details this service composes its invoice description from.
   *
   * Local state rather than a react-hook-form field: it is a set of
   * checkboxes with no validation message of its own — an empty selection is
   * a valid, meaningful answer (compose nothing) rather than a missing value.
   */
  const [detailFields, setDetailFields] = useState<string[]>([]);

  const toggleDetailField = (key: string) =>
    setDetailFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  /**
   * The picked client. DirectorySelect is told what to show rather than
   * remembering it, so the name is held here beside the id — and an empty id
   * is what "General" means on this form.
   */
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
    null,
  );
  const [selectedPartnerName, setSelectedPartnerName] = useState("");
  /** Set when an existing client rate is left with no client to save under. */
  const [partnerError, setPartnerError] = useState(false);

  /**
   * Where the last stopped Create/Update raised its message, so it can be
   * scrolled into view. These checks report away from the button — the matrix
   * one at the top of its section, the client one at the top of the form — and
   * an error the user cannot see makes the button look like it did nothing.
   * Fields react-hook-form can focus are left to it; this covers the rest.
   *
   * The counter is what triggers the scroll, so pressing Create again with the
   * same problem scrolls again even though the error state does not change.
   */
  const errorTargetRef = useRef<"partner" | "serviceCode" | "matrix" | null>(
    null,
  );
  const [errorScrollTick, setErrorScrollTick] = useState(0);
  const partnerFieldRef = useRef<HTMLDivElement>(null);
  const serviceCodeFieldRef = useRef<HTMLDivElement>(null);
  const matrixErrorsRef = useRef<HTMLDivElement>(null);

  const revealError = (target: "partner" | "serviceCode" | "matrix") => {
    errorTargetRef.current = target;
    setErrorScrollTick((n) => n + 1);
  };

  // After the render that shows the message — the matrix list only exists once
  // its errors are set.
  useEffect(() => {
    if (!errorScrollTick || !errorTargetRef.current) return;
    const target = {
      partner: partnerFieldRef,
      serviceCode: serviceCodeFieldRef,
      matrix: matrixErrorsRef,
    }[errorTargetRef.current].current;
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [errorScrollTick]);
  /**
   * The general service a new client-specific rate was copied from. Held only
   * to drive the form (which fields are locked, what the picker shows) — the
   * values are copied into the new record, and no reference to this service is
   * stored, so the two can be edited independently from here on.
   */
  const [sourceService, setSourceService] = useState<Service | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(createServiceSchema) as any,
    defaultValues: {
      name: "",
      description: "",
      type: "HOUR",
      price: "",
      tax: "NO_TAX",
      partnerId: null,
      ...EMPTY_ACCOUNTING_FIELDS,
    },
  });

  const watchedType = watch("type");
  const isMatrix = watchedType === "MATRIX";

  /** An empty client box is General — the other side of the directory. */
  const isClientSpecific = !!selectedPartnerId;
  /** Editing a row that is already a client rate, rather than a general one. */
  const isStoredClientRate = isEditMode && !!service?.partnerId;
  /**
   * A rate for a client is always copied from an existing general service, so
   * every client price maps back to a code on the general rate card. That is
   * what swaps the free-text code box for the picker — on the edit form too,
   * so the code can be corrected later without leaving the rate card.
   */
  const usesGeneralTemplate = isClientSpecific;
  /**
   * Create only: a client is selected but no code picked yet, so there is
   * nothing to price. An existing rate always has a code already.
   */
  const awaitingTemplate = !isEditMode && usesGeneralTemplate && !sourceService;
  /**
   * The copied catalogue fields — unit type, tax and a matrix's zones and bands
   * — belong to the general service, not to the client's rate. A client rate
   * only ever restates the price and description; to change the rest, point it
   * at a different code.
   */
  const templateLocked = isClientSpecific && (isEditMode || !!sourceService);

  /**
   * A General service cannot be given a client. Saving moves the row rather
   * than copying it, so it would leave the general rate card and become a
   * client rate with no code behind it. The client's own rate is added from the
   * Client Specific side, copied from this code — so the field is not part of
   * this form at all, rather than a dead box inviting the question.
   *
   * A client rate keeps its field: it can move to another client, the code
   * travelling with it. It just cannot become a general service, which would
   * duplicate the code it was copied from — hence no empty client on save.
   */
  const partnerHidden = isEditMode && !isStoredClientRate;

  // Keep react-hook-form in sync when the picked client changes
  const handlePartnerSelect = (id: string, companyName: string) => {
    setSelectedPartnerId(id);
    setSelectedPartnerName(companyName);
    setValue("partnerId", id);
    setPartnerError(false);
  };

  /**
   * On the create form an empty client is not a missing value — it is General,
   * the other side of the directory — so the code box goes back to free text.
   * Whatever a template filled in stays as a starting point rather than being
   * wiped from under the user.
   */
  const handlePartnerClear = () => {
    setSelectedPartnerId(null);
    setSelectedPartnerName("");
    setValue("partnerId", null);
    if (!isEditMode && sourceService) {
      setSourceService(null);
      // The code came off the general rate card, so it is already taken there
      // and cannot be the code of a new general service. Only the code is
      // dropped — the price, tax and description stay as a starting point.
      setValue("name", "");
    }
  };

  /**
   * Copy a general service into the form. Everything is copied by value — the
   * new record is a snapshot, not a pointer, so editing either side later
   * leaves the other alone.
   */
  const applyGeneralService = (general: Service) => {
    setSourceService(general);

    setValue("name", general.name, { shouldValidate: true });
    setValue("type", general.type, { shouldValidate: true });
    setValue("tax", general.tax || "NO_TAX", { shouldValidate: true });
    setValue("description", general.description || "");
    setValue("price", general.price != null ? String(general.price) : "", {
      shouldValidate: true,
    });
    // Copied like the price: a starting point for this client, not a lock. A
    // client can be booked against a different object or receivable account,
    // so all three stay editable — they just cannot be emptied.
    const copied = accountingFields(general);
    setValue("accountingVatCode", copied.accountingVatCode, {
      shouldValidate: true,
    });
    setValue("accountingIncomeAccount", copied.accountingIncomeAccount, {
      shouldValidate: true,
    });
    // Copied like the price and the accounting codes: a starting point for
    // this client, and editable — one client may want the BL number on their
    // invoices where another wants the booking.
    setDetailFields(general.invoiceDetails?.fields ?? []);
    onTypeChange?.(general.type);

    if (general.type === "MATRIX") {
      setZones(toFormZones(general));
      setBands(toFormBands(general));
      setPrices(toIndexedPrices(general));
    } else {
      setZones([]);
      setBands([]);
      setPrices({});
    }
    setMatrixErrors([]);
  };

  /**
   * Drop the picked code. On the edit form that puts the rate back on the code
   * it is saved under, values and all; on the create form it goes back to a
   * blank slate, since a client rate has no fields of its own until a code is
   * picked.
   */
  const clearGeneralService = () => {
    setSourceService(null);

    const base = isEditMode ? service : undefined;
    const baseType = base?.type ?? "HOUR";

    setValue("name", base?.name ?? "");
    setValue("type", baseType);
    setValue("tax", base?.tax ?? "NO_TAX");
    setValue("description", base?.description ?? "");
    setValue("price", base?.price != null ? String(base.price) : "");
    const reverted = accountingFields(base);
    setValue("accountingVatCode", reverted.accountingVatCode);
    setValue("accountingIncomeAccount", reverted.accountingIncomeAccount);
    setDetailFields(base?.invoiceDetails?.fields ?? []);
    onTypeChange?.(baseType);

    if (base && base.type === "MATRIX") {
      setZones(toFormZones(base));
      setBands(toFormBands(base));
      setPrices(toIndexedPrices(base));
    } else {
      setZones([]);
      setBands([]);
      setPrices({});
    }
    setMatrixErrors([]);
  };

  useEffect(() => {
    if (service && isEditMode) {
      reset({
        name: service.name,
        description: service.description || "",
        type: service.type,
        price: service.price != null ? String(service.price) : "",
        tax: service.tax || "NO_TAX",
        partnerId: service.partnerId || null,
        // Blank on services stored before the mapping existed — the form asks
        // for all three, so the first edit fills them in.
        ...accountingFields(service),
      });

      setDetailFields(service.invoiceDetails?.fields ?? []);
      setSelectedPartnerId(service.partnerId || null);
      setSelectedPartnerName(service.partnerInfo?.name || "");
      setPartnerError(false);
      setSourceService(null);

      if (service.type === "MATRIX") {
        setZones(toFormZones(service));
        setBands(toFormBands(service));
        setPrices(toIndexedPrices(service));
      } else {
        setZones([]);
        setBands([]);
        setPrices({});
      }
    } else if (!isEditMode) {
      reset({
        name: "",
        description: "",
        type: "HOUR",
        price: "",
        tax: "NO_TAX",
        partnerId: null,
        ...EMPTY_ACCOUNTING_FIELDS,
      });
      setDetailFields([]);
      setSelectedPartnerId(null);
      setSelectedPartnerName("");
      setPartnerError(false);
      setSourceService(null);
      setZones([]);
      setBands([]);
      setPrices({});
    }
  }, [service, isEditMode, reset]);

  // Zone helpers
  const addZone = () =>
    setZones((prev) => [...prev, { name: "", postalFrom: "", postalTo: "" }]);

  const removeZone = (idx: number) => {
    setZones((prev) => prev.filter((_, i) => i !== idx));
    setPrices((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([key, val]) => {
        const [z, b] = key.split("-").map(Number);
        if (z < idx) next[key] = val;
        else if (z > idx) next[`${z - 1}-${b}`] = val;
      });
      return next;
    });
  };

  const updateZone = (idx: number, field: keyof FormZone, value: string) =>
    setZones((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });

  const applyZonePreset = (idx: number, preset: FormZone) =>
    setZones((prev) => {
      const next = [...prev];
      next[idx] = { ...preset };
      return next;
    });

  const prefillAllCounties = () => setZones([...PREFILLED_ZONES]);

  // Band helpers
  const addBand = (count: number = 1) => {
    const newBands: FormBand[] = Array.from({ length: count }, () => ({
      label: "",
      minTn: "",
      maxTn: "",
      ldm: "",
    }));
    setBands((prev) => [...prev, ...newBands]);
  };

  const removeBand = (idx: number) => {
    setBands((prev) => prev.filter((_, i) => i !== idx));
    setPrices((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([key, val]) => {
        const [z, b] = key.split("-").map(Number);
        if (b < idx) next[key] = val;
        else if (b > idx) next[`${z}-${b - 1}`] = val;
      });
      return next;
    });
  };

  const updateBand = (idx: number, field: keyof FormBand, value: string) => {
    setBands((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handlePriceChange = (key: string, value: string) =>
    setPrices((prev) => ({ ...prev, [key]: value }));

  const validateMatrix = useCallback((): boolean => {
    const errs: string[] = [];
    if (zones.length === 0) errs.push(t("services:form.matrixAtLeastOneZone"));
    if (bands.length === 0) errs.push(t("services:form.matrixAtLeastOneBand"));

    zones.forEach((z, i) => {
      if (!z.name.trim())
        errs.push(t("services:form.zoneNameRequired", { index: i + 1 }));
      if (!z.postalFrom.trim())
        errs.push(t("services:form.zonePostalFromRequired", { index: i + 1 }));
      if (!z.postalTo.trim())
        errs.push(t("services:form.zonePostalToRequired", { index: i + 1 }));
    });

    bands.forEach((b, i) => {
      if (b.minTn === "" || b.maxTn === "" || b.ldm === "") {
        errs.push(t("services:form.bandFieldsRequired", { index: i + 1 }));
      }
    });

    for (let z = 0; z < zones.length; z++) {
      for (let b = 0; b < bands.length; b++) {
        const val = parseFloat(prices[`${z}-${b}`] || "");
        if (isNaN(val) || val <= 0) {
          errs.push(
            t("services:form.priceMustBePositive", {
              zone:
                zones[z].name || `${t("services:form.zonePrefix")} ${z + 1}`,
              band:
                bands[b].label || `${t("services:form.bandPrefix")} ${b + 1}`,
            }),
          );
        }
      }
    }

    setMatrixErrors(errs);
    return errs.length === 0;
  }, [zones, bands, prices, t]);

  const onFormSubmit = async (formData: FormValues) => {
    // A rate filed under a client has to stay filed under one. Clearing the box
    // is how you would try to make it a general service, which is the one move
    // between the two sides of the directory that is never allowed — so it is
    // an error to fix rather than something to save around.
    if (isStoredClientRate && !selectedPartnerId) {
      setPartnerError(true);
      revealError("partner");
      return;
    }

    // Use the controlled selectedPartnerId instead of formData.partnerId (they
    // are kept in sync via setValue, but this is explicit). A General service
    // keeps its empty client: the box is read-only there, so this is only the
    // backstop — the server refuses the move as well.
    const resolvedPartnerId = partnerHidden
      ? service?.partnerId || null
      : selectedPartnerId;

    // Trimmed here rather than relied on from Yup: Yup validates the trimmed
    // value but hands back what was typed.
    const accounting: ServiceAccounting = {
      vatCode: formData.accountingVatCode.trim(),
      incomeAccount: formData.accountingIncomeAccount.trim(),
    };

    if (isMatrix) {
      if (!validateMatrix()) {
        revealError("matrix");
        return;
      }

      const builtZones: Omit<Zone, "_id">[] = zones.map((z) => ({
        name: z.name,
        postalFrom: z.postalFrom,
        postalTo: z.postalTo,
      }));
      const builtBands: Omit<Band, "_id">[] = bands.map((b) => ({
        label: b.label,
        minTn: parseFloat(String(b.minTn)),
        maxTn: parseFloat(String(b.maxTn)),
        ldm: parseFloat(String(b.ldm)),
      }));
      const builtMatrix: PriceMatrixIndexCell[] = [];
      for (let z = 0; z < zones.length; z++) {
        for (let b = 0; b < bands.length; b++) {
          builtMatrix.push({
            zoneIndex: z,
            bandIndex: b,
            price: parseFloat(prices[`${z}-${b}`]),
          });
        }
      }

      return onSubmit({
        name: formData.name,
        description: formData.description || "",
        type: "MATRIX",
        tax: formData.tax as any,
        accounting,
        invoiceDetails: { fields: detailFields },
        partnerId: resolvedPartnerId,
        zones: builtZones as Zone[],
        bands: builtBands as Band[],
        priceMatrix: builtMatrix as any,
      });
    }

    return onSubmit({
      name: formData.name,
      description: formData.description || "",
      type: formData.type as any,
      // An empty box is null, NOT zero. Null says this service has no default
      // rate and is priced when it is charged; zero says it is free. They are
      // the same arithmetic and opposite instructions to whoever fills in the
      // charge row, so the blank must survive the save rather than be rounded
      // into a number. Sent explicitly (not omitted) so that clearing a price
      // on an existing service reaches the server as a value — the update is
      // PATCH-shaped, and an absent key means "leave it alone".
      price: formData.price?.trim() ? parseFloat(formData.price) : null,
      tax: formData.tax as any,
      accounting,
      invoiceDetails: { fields: detailFields },
      partnerId: resolvedPartnerId,
    });
  };

  const SERVICE_TYPE_OPTIONS = [
    { value: "HOUR", label: "HOUR" },
    { value: "PIECE", label: "PIECE" },
    { value: "LIFT", label: "LIFT" },
    { value: "KM", label: "KM" },
    { value: "FLAT", label: "FLAT" },
    { value: "QTY", label: "QTY" },
    { value: "MATRIX", label: "Matrix" },
  ];

  const TAX_OPTIONS = [
    { value: "NO_TAX", label: "No Tax" },
    { value: "0", label: "0%" },
    { value: "24", label: "24%" },
  ];

  // ---------------------------------------------------------------------------
  // Read-only view (edit mode, before user clicks "Edit")
  // ---------------------------------------------------------------------------
  if (isEditMode && readonly) {
    const isMatrixView = service?.type === "MATRIX";

    return (
      <div className="mx-auto bg-gray-50 rounded-md overflow-hidden">
        <div className="p-3 space-y-2 overflow-y-auto">
          <SectionCard icon={Wrench} title={t("services:form.serviceCode")}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px]">
              {service?.partnerInfo && (
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="w-32 text-gray-500 text-[12px]">
                    {t("services:form.client")}
                  </dt>
                  <dd className="text-gray-900 flex items-center gap-2">
                    {service.partnerInfo.name}
                    {service.partnerInfo.isDeleted && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                        {t("services:form.deleted")}
                      </span>
                    )}
                  </dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.serviceCode")}
                </dt>
                <dd className="text-gray-900 font-medium" title={service?.name}>
                  {service?.name && service.name.length > 15
                    ? `${service.name.substring(0, 15)}...`
                    : service?.name}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.unitType")}
                </dt>
                <dd className="text-gray-900">
                  {service?.type || t("services:form.notSpecified")}
                </dd>
              </div>
              {!isMatrixView && (
                <div className="flex gap-2">
                  <dt className="w-32 text-gray-500 text-[12px]">
                    {t("services:form.price")}
                  </dt>
                  <dd className="text-gray-900">
                    {service?.price != null
                      ? `€${service.price.toFixed(2)}`
                      : t("services:form.notSpecified")}
                  </dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.tax")}
                </dt>
                <dd className="text-gray-900">
                  {service?.tax || t("services:form.noTax")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.vatCode")}
                </dt>
                <dd className="text-gray-900">
                  {service?.accounting?.vatCode ||
                    t("services:form.notSpecified")}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.incomeAccount")}
                </dt>
                <dd className="text-gray-900">
                  {service?.accounting?.incomeAccount ||
                    t("services:form.notSpecified")}
                </dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.description")}
                </dt>
                <dd className="text-gray-900 whitespace-pre-wrap">
                  {service?.description || t("services:form.noDescription")}
                </dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="w-32 text-gray-500 text-[12px]">
                  {t("services:form.invoiceDetails")}
                </dt>
                <dd className="text-gray-900">
                  {service?.invoiceDetails?.fields?.length ? (
                    <span className="font-mono text-[12px]">
                      {previewDetailDescription(service.invoiceDetails.fields)}
                    </span>
                  ) : (
                    t("services:form.invoiceDetailsNone")
                  )}
                </dd>
              </div>
            </dl>
          </SectionCard>

          {isMatrixView && service?.zones && service?.bands && (
            <SectionCard
              icon={Grid3x3}
              title={t("services:form.matrixPricing")}
            >
              <PriceTable
                zones={toFormZones(service)}
                bands={toFormBands(service)}
                prices={toIndexedPrices(service)}
                onChange={() => {}}
                readonly
                weightLdmLabel={t("services:form.weightLdm")}
                zonePrefix={t("services:form.zonePrefix")}
                bandPrefix={t("services:form.bandPrefix")}
                allCellsNote={t("services:form.allCellsMustBeFilled")}
              />
            </SectionCard>
          )}
        </div>

        {!service?.partnerInfo?.isDeleted && (
          <div className="bg-white px-4 py-2.5 border-t border-gray-200 flex justify-end">
            <Button
              type="button"
              onClick={() => setReadonly(false)}
              variant="primary"
              icon={<Pencil className="w-3.5 h-3.5" />}
            >
              {t("common:buttons.edit")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit / Create form
  // ---------------------------------------------------------------------------
  return (
    <form
      onSubmit={handleSubmit(onFormSubmit, (formErrors) => {
        // On a client rate the code is a picker, not an input react-hook-form
        // can focus, so its error would stay wherever the form is scrolled to.
        if (formErrors.name && usesGeneralTemplate) revealError("serviceCode");
      })}
    >
      <div className="p-3 space-y-2">
        <SectionCard icon={Wrench} title={t("services:form.serviceCode")}>
          <div className="space-y-1">
            {/* A general service has no client and cannot be given one, so the
                field is left out of its edit form entirely rather than shown
                empty and dead — the read-only view omits it for the same
                reason. It stays on the create form, where an empty client is a
                real choice: it is what files the service under General. */}
            {!partnerHidden && (
              <FormField
                layout="inline"
                size="sm"
                label={t("services:form.client")}
              >
                <div ref={partnerFieldRef} className="space-y-1">
                  <DirectorySelect
                    source="partners"
                    selectedId={selectedPartnerId}
                    value={selectedPartnerName}
                    onSelect={(snapshot) =>
                      handlePartnerSelect(snapshot._id, snapshot.companyName)
                    }
                    onClear={handlePartnerClear}
                    disabled={readonly}
                    size="sm"
                    error={partnerError}
                    placeholder={t("services:form.selectGeneral")}
                    // Services are priced for clients that already exist;
                    // adding one to the directory belongs on the Partners page.
                    allowCreate={false}
                  />
                  <p
                    className={`text-[11px] ${
                      partnerError ? "text-[#E24B4A]" : "text-gray-500"
                    }`}
                  >
                    {partnerError
                      ? t("services:form.clientRequiredOnClientRate")
                      : isEditMode
                        ? t("services:form.clientMovableNotGeneral")
                        : t("services:form.emptyClientIsGeneral")}
                  </p>
                </div>
              </FormField>
            )}

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.serviceCode")}
              required
              error={errors.name?.message}
            >
              {usesGeneralTemplate ? (
                <div ref={serviceCodeFieldRef} className="space-y-1">
                  <GeneralServiceSelect
                    value={sourceService}
                    currentLabel={isEditMode ? service?.name : undefined}
                    onSelect={applyGeneralService}
                    onClear={clearGeneralService}
                    fetchGeneralServices={fetchGeneralServices}
                    disabled={readonly}
                    error={!!errors.name}
                    placeholder={t("services:form.selectGeneralService")}
                  />
                  <p className="text-[11px] text-gray-500">
                    {sourceService
                      ? t("services:form.copiedFromGeneral", {
                          code: sourceService.serviceId,
                        })
                      : isEditMode
                        ? t("services:form.recodeGeneralServiceHint")
                        : t("services:form.pickGeneralServiceHint")}
                  </p>
                </div>
              ) : (
                <Input
                  {...register("name")}
                  size="sm"
                  error={!!errors.name}
                  placeholder={t("services:form.enterServiceCode")}
                  readOnly={readonly || templateLocked}
                />
              )}
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.unitType")}
              required
              error={errors.type?.message}
            >
              <Select
                {...register("type")}
                size="sm"
                options={SERVICE_TYPE_OPTIONS}
                // Native `disabled` (not register's `disabled` option, which
                // would blank the value) — RHF keeps what applyGeneralService
                // stored, so the copied type still submits.
                disabled={readonly || usesGeneralTemplate || templateLocked}
                placeholder={t("services:form.selectType")}
                onChange={(e) => {
                  register("type").onChange(e);
                  onTypeChange?.(e.target.value);
                }}
              />
            </FormField>

            {!isMatrix && (
              <FormField
                layout="inline"
                size="sm"
                label={t("services:form.price")}
                error={errors.price?.message}
              >
                <Input
                  {...register("price")}
                  type="number"
                  step="0.01"
                  size="sm"
                  placeholder="0.00"
                  error={!!errors.price}
                  readOnly={readonly || awaitingTemplate}
                />
              </FormField>
            )}

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.tax")}
              required
              error={errors.tax?.message}
            >
              <Select
                {...register("tax")}
                size="sm"
                options={TAX_OPTIONS}
                // Locked alongside the unit type: the tax is the general
                // service's, carried over with the code, so a client rate
                // restates the price rather than the tax. Native `disabled`
                // keeps the copied value in RHF, so it still submits.
                disabled={readonly || usesGeneralTemplate || templateLocked}
                placeholder={t("services:form.selectTax")}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.description")}
              error={errors.description?.message}
            >
              <Textarea
                {...register("description")}
                size="sm"
                error={!!errors.description}
                placeholder={t("services:form.enterDescription")}
                readOnly={readonly || awaitingTemplate}
                rows={3}
              />
            </FormField>
          </div>
        </SectionCard>

        {/* Its own card rather than three more boxes under the price: these
            codes answer a different question (where the money is booked, not
            what it costs) and are the ones an accountant checks. On a client
            rate they are copied from the general service and stay editable —
            a client can be booked against a different object or receivable
            account — so unlike the unit type and tax they are never locked. */}
        <SectionCard icon={Calculator} title={t("services:form.accounting")}>
          <div className="space-y-1">
            {/* Said once above the three boxes rather than under each. */}
            <p className="pb-1 text-[11px] text-gray-500">
              {isEditMode && !service?.accounting?.vatCode
                ? t("services:form.accountingMissing")
                : isClientSpecific
                  ? t("services:form.accountingClientHint")
                  : t("services:form.accountingHint")}
            </p>

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.vatCode")}
              required
              error={errors.accountingVatCode?.message}
            >
              <Input
                {...register("accountingVatCode")}
                size="sm"
                error={!!errors.accountingVatCode}
                placeholder={t("services:form.enterVatCode")}
                readOnly={readonly || awaitingTemplate}
              />
            </FormField>

            <FormField
              layout="inline"
              size="sm"
              label={t("services:form.incomeAccount")}
              required
              error={errors.accountingIncomeAccount?.message}
            >
              <Input
                {...register("accountingIncomeAccount")}
                size="sm"
                error={!!errors.accountingIncomeAccount}
                placeholder={t("services:form.enterIncomeAccount")}
                readOnly={readonly || awaitingTemplate}
              />
            </FormField>
          </div>
        </SectionCard>

        {/* What an invoice line for this service is CALLED, as opposed to what
            it costs. Ticking nothing is the default and a real answer: the
            line keeps the description the service row wrote. Ticking any of
            them replaces it with the container's own identifiers, which is
            why the preview below shows the shape rather than describing it.

            Container details only, for now — a service billed on a shipment
            or a work keeps its description either way, which the hint says
            rather than leaving the user to discover it on an invoice. */}
        <SectionCard icon={ReceiptText} title={t("services:form.invoiceDetails")}>
          <div className="space-y-2">
            <p className="text-[11px] text-gray-500">
              {t("services:form.invoiceDetailsHint")}
            </p>

            <div className="divide-y divide-[rgba(11,15,14,0.06)] rounded-md border border-[rgba(11,15,14,0.1)]">
              {INVOICE_DETAIL_KEYS.map((key) => {
                const checked = detailFields.includes(key);
                return (
                  <label
                    key={key}
                    className={`flex items-center justify-between gap-3 px-3 py-2 text-[13px] ${
                      readonly || awaitingTemplate
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-[#FAF9F7]"
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={readonly || awaitingTemplate}
                        onChange={() => toggleDetailField(key)}
                        className="h-4 w-4 shrink-0 accent-[#C97A3C]"
                      />
                      <span
                        className={`truncate ${
                          checked ? "font-medium text-[#0B0F0E]" : "text-gray-700"
                        }`}
                      >
                        {t(`services:form.detail.${key}`)}
                      </span>
                    </span>
                    {/* An example, not this service's data — the form is
                        configuring a service and has no container in front of
                        it. It is here to show the shape of what you opt into. */}
                    <span className="shrink-0 font-mono text-[11px] text-gray-400">
                      {INVOICE_DETAIL_SAMPLES[key]}
                    </span>
                  </label>
                );
              })}
            </div>

            {detailFields.length > 0 ? (
              <div className="rounded-md bg-[#F5F4F0] px-3 py-2">
                <p className="text-[11px] text-gray-500">
                  {t("services:form.invoiceDetailsPreview")}
                </p>
                <p className="mt-0.5 font-mono text-[12px] text-[#0B0F0E] break-words">
                  {previewDetailDescription(detailFields)}
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-gray-500">
                {t("services:form.invoiceDetailsEmptyHint")}
              </p>
            )}
          </div>
        </SectionCard>

        {isMatrix && (
          <SectionCard icon={Grid3x3} title={t("services:form.matrixPricing")}>
            <div className="space-y-3">
              {matrixErrors.length > 0 && (
                <div
                  ref={matrixErrorsRef}
                  className="bg-red-50 border border-red-200 rounded-md p-3"
                >
                  <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                    {matrixErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Zones */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    {t("services:form.zones")}{" "}
                    <span className="text-gray-400 font-normal">
                      {t("services:form.zonesHint")}
                    </span>
                  </p>
                  {/* Copied from a general service: the price matrix has one
                      cell per zone × band, so adding or dropping either would
                      no longer match what was copied in. */}
                  {!templateLocked && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={prefillAllCounties}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        {t("services:form.prefillCounties")}
                      </button>
                      <button
                        type="button"
                        onClick={addZone}
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t("services:form.addZone")}
                      </button>
                    </div>
                  )}
                </div>

                {zones.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    {t("services:form.noZonesYet")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {zones.map((zone, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2"
                      >
                        <ZoneCombobox
                          value={zone.name}
                          onChange={(name) => updateZone(idx, "name", name)}
                          onSelect={(preset) => applyZonePreset(idx, preset)}
                          placeholder={t("services:form.zoneNamePlaceholder")}
                          disabled={templateLocked}
                        />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {t("services:form.from")}
                          </span>
                          <input
                            type="text"
                            value={zone.postalFrom}
                            onChange={(e) =>
                              updateZone(
                                idx,
                                "postalFrom",
                                e.target.value.slice(0, 6),
                              )
                            }
                            maxLength={6}
                            disabled={templateLocked}
                            placeholder={t(
                              "services:form.postalFromPlaceholder",
                            )}
                            className={`w-[72px] text-sm text-center border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              templateLocked ? LOCKED_FIELD_CLASSES : ""
                            }`}
                          />
                          <span className="text-gray-400">–</span>
                          <input
                            type="text"
                            value={zone.postalTo}
                            onChange={(e) =>
                              updateZone(
                                idx,
                                "postalTo",
                                e.target.value.slice(0, 6),
                              )
                            }
                            maxLength={6}
                            disabled={templateLocked}
                            placeholder={t("services:form.postalToPlaceholder")}
                            className={`w-[72px] text-sm text-center border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              templateLocked ? LOCKED_FIELD_CLASSES : ""
                            }`}
                          />
                        </div>
                        {!templateLocked && (
                          <button
                            type="button"
                            onClick={() => removeZone(idx)}
                            className="text-gray-400 hover:text-red-500 shrink-0 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bands */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    {t("services:form.weightBands")}{" "}
                    <span className="text-gray-400 font-normal">
                      {t("services:form.weightBandsHint")}
                    </span>
                  </p>
                  {!templateLocked && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        defaultValue={1}
                        id="band-count-input"
                        className="w-14 text-sm border border-gray-300 rounded-md px-2 py-1.5 text-center"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById(
                            "band-count-input",
                          ) as HTMLInputElement;
                          const count = parseInt(el?.value || "1", 10);
                          addBand(isNaN(count) || count < 1 ? 1 : count);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t("services:form.addBand")}
                      </button>
                    </div>
                  )}
                </div>

                {bands.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    {t("services:form.noBandsYet")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {bands.map((band, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2"
                      >
                        <Input
                          value={band.label}
                          onChange={(e) =>
                            updateBand(idx, "label", e.target.value)
                          }
                          placeholder={t("services:form.bandLabelPlaceholder")}
                          className="flex-1 min-w-0"
                          size="sm"
                          readOnly={templateLocked}
                        />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            Min
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={templateLocked}
                            value={String(band.minTn)}
                            onChange={(e) =>
                              updateBand(idx, "minTn", e.target.value)
                            }
                            placeholder="0"
                            className={`w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              templateLocked ? LOCKED_FIELD_CLASSES : ""
                            }`}
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            Max
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={templateLocked}
                            value={String(band.maxTn)}
                            onChange={(e) =>
                              updateBand(idx, "maxTn", e.target.value)
                            }
                            placeholder="3"
                            className={`w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              templateLocked ? LOCKED_FIELD_CLASSES : ""
                            }`}
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {t("services:form.ldm")}
                          </span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            disabled={templateLocked}
                            value={String(band.ldm)}
                            onChange={(e) =>
                              updateBand(idx, "ldm", e.target.value)
                            }
                            placeholder="1.6"
                            className={`w-[60px] text-sm text-center border border-gray-300 rounded-md px-1 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              templateLocked ? LOCKED_FIELD_CLASSES : ""
                            }`}
                          />
                        </div>
                        {!templateLocked && (
                          <button
                            type="button"
                            onClick={() => removeBand(idx)}
                            className="text-gray-400 hover:text-red-500 shrink-0 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {zones.length > 0 && bands.length > 0 && (
                <div className="w-full">
                  <p className="text-[12px] font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    {t("services:form.priceMatrix")}
                  </p>
                  <PriceTable
                    zones={zones}
                    bands={bands}
                    prices={prices}
                    onChange={handlePriceChange}
                    weightLdmLabel={t("services:form.weightLdm")}
                    zonePrefix={t("services:form.zonePrefix")}
                    bandPrefix={t("services:form.bandPrefix")}
                    allCellsNote={t("services:form.allCellsMustBeFilled")}
                  />
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>

      <div className="flex justify-end items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {isEditMode && readonly && (
          <Button
            type="button"
            onClick={() => setReadonly(false)}
            variant="outline"
            icon={<Pencil className="w-3.5 h-3.5" />}
          >
            {t("common:buttons.edit")}
          </Button>
        )}
        {(!isEditMode || (isEditMode && !readonly)) && (
          <Button
            type="submit"
            isLoading={loading}
            disabled={loading}
            variant="primary"
          >
            {isEditMode
              ? t("common:buttons.saveChanges")
              : t("common:buttons.create")}
          </Button>
        )}
      </div>
    </form>
  );
};
